import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { IStorePackage } from 'models/store/i-store-package.interface';
import { IStorePackagesHookResponse } from 'models/store/i-store-packages-pagination.interface';

import { BREAKPOINTS } from 'constants/defaults.constant';
import { STORE_PAGINATION } from 'constants/store.constant';

import { addErrorMessageAction } from 'actions/global-actions';

import { useWindowSize } from 'hooks/useWindowSize';

import { calculatePagination, getItemsPerPage } from 'utils/store-packages';

import { queryKeys } from './query-keys';

// Fetch store packages
const fetchStorePackages = async (): Promise<IStorePackage[]> => {
  const response = await fetch('/api/v1/trades/packages/');
  if (!response.ok) {
    throw new Error('Failed to fetch store packages');
  }
  return response.json();
};

export const useStorePackagesQuery = (): IStorePackagesHookResponse => {
  const dispatch = useDispatch();
  const [width] = useWindowSize();
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Calculate items per page based on window size
  const itemsPerPage = getItemsPerPage(
    width,
    BREAKPOINTS.MOBILE_BREAKPOINT,
    STORE_PAGINATION.ITEMS_PER_PAGE_MOBILE,
    STORE_PAGINATION.ITEMS_PER_PAGE_DESKTOP
  );

  // Query for store packages
  const {
    data: allPackages = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: queryKeys.storePackages.list(),
    queryFn: fetchStorePackages,
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        (error as any)?.response?.data?.message || error?.message || 'Failed to fetch store packages';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [error, dispatch]);

  // Reset to page 1 when items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Calculate pagination
  const pagination = calculatePagination(allPackages, currentPage, itemsPerPage);

  // Pagination functions
  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= pagination.totalPages) {
        setCurrentPage(page);
      }
    },
    [pagination.totalPages]
  );

  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [pagination.hasNextPage]);

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [pagination.hasPreviousPage]);

  const refresh = useCallback(() => {
    setCurrentPage(1);
    // React Query will handle the refresh automatically with invalidateQueries
  }, []);

  return {
    data: pagination.data,
    currentPage,
    totalPages: pagination.totalPages,
    totalItems: pagination.totalItems,
    itemsPerPage,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    shouldShowPagination: pagination.shouldShowPagination,
    loading,
    error: error ? String(error) : null,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    refresh,
  };
};
